import axios from 'axios'

/**
 * Single home for HTTP-error-shape knowledge. Screens ask named questions
 * instead of reaching into `error.response.status`, so the axios dependency and
 * every status code the UI reacts to live here and nowhere else.
 */

const statusOf = (error: unknown): number | undefined =>
	axios.isAxiosError(error) ? error.response?.status : undefined

/** 400 — the request failed server-side validation. */
export const isBadRequest = (error: unknown): boolean => statusOf(error) === 400

/** 404 — the looked-up resource does not exist. */
export const isNotFound = (error: unknown): boolean => statusOf(error) === 404

/** 409 — the change conflicts with current server state. */
export const isConflict = (error: unknown): boolean => statusOf(error) === 409

/** 413 — the upload exceeds the request size limit. */
export const isPayloadTooLarge = (error: unknown): boolean => statusOf(error) === 413

/** 429 — the caller is rate limited. */
export const isRateLimited = (error: unknown): boolean => statusOf(error) === 429

/** 441 — Orbit's non-standard "leaving is blocked by active expenses/pay-ups". */
export const isLeaveBlockedByBalances = (error: unknown): boolean => statusOf(error) === 441

/** Any 4xx — the request itself is at fault, so retrying cannot succeed. */
export const isClientError = (error: unknown): boolean => {
	const status = statusOf(error)
	return status !== undefined && status >= 400 && status < 500
}

/** 5xx — the server failed to handle an otherwise-valid request. */
export const isServerError = (error: unknown): boolean => {
	const status = statusOf(error)
	return status !== undefined && status >= 500
}

/** The HTTP response carried by an API error, in transport-agnostic shape. */
export interface ApiErrorResponse {
	status: number
	data: unknown
	headers: Record<string, unknown>
}

/** The response carried by an API error, or undefined for non-HTTP failures. */
export const getApiErrorResponse = (error: unknown): ApiErrorResponse | undefined => {
	if (axios.isAxiosError(error) && error.response) {
		return {
			status: error.response.status,
			data: error.response.data,
			headers: (error.response.headers ?? {}) as Record<string, unknown>,
		}
	}
	return undefined
}

/**
 * Maps any thrown value to a user-facing message. Distinguishes network failures
 * and 5xx responses from server-supplied messages, falling back to
 * `defaultMessage` when the error shape is unknown.
 */
export const getErrorMessage = (error: unknown, defaultMessage: string): string => {
	if (axios.isAxiosError(error)) {
		// Network error (no internet, connection refused, etc.)
		if (!error.response) {
			return 'Network error. Please check your internet connection and try again.'
		}
		// Server error (error response from server)
		const status = error.response?.status
		const message = error.response?.data?.message || error.message

		if (status && status >= 500) {
			return 'Server error. Please try again later.'
		}

		return message || defaultMessage
	}

	// Generic error
	return defaultMessage
}
